<!--@format-->
<template>
    <div class="edit-proof-of-payment-widget">
        <div class="widget-container">
            <div class="widget-header">
                <h3>{{ title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div>
                        <p>Replace or remove your proof of payment</p>
                        <div v-if="isProofDeleted" class="proof-deleted-container">
                            <div class="delete-icon">
                                <e25-deleted-items-svg color="#f30070" />
                            </div>
                            <p>Your uploaded document was deleted</p>
                        </div>
                        <div v-else class="proof-file-container">
                            <div>
                                <label>Uploaded Document</label>
                                <app-files-preview
                                    :files="
                                        initialProofOfPayment.file ? [initialProofOfPayment] : []
                                    "
                                >
                                    <template v-if="!isNewFileUploaded" slot="actions">
                                        <app-icon-button
                                            class="delete-action"
                                            @click="handleDeleteFile"
                                        >
                                            <e25-deleted-items-svg color="#f30070" />
                                        </app-icon-button>
                                    </template>
                                </app-files-preview>
                            </div>
                            <hr />
                            <div>
                                <label>Replace document with</label>
                                <app-drag-and-drop-files
                                    files-title=""
                                    label="Or Drag File Here"
                                    library-enabled
                                    hide-area-on-upload
                                    hide-delete-on-upload
                                    :files="proofOfPayment.file ? [proofOfPayment] : []"
                                    :file-accept="fileConfig.fileSelectorAllowedTypes"
                                    :loading-files="isLoadingFiles"
                                    :library-file-types="fileConfig.fileSelectorAllowedTypes"
                                    @upload="handleUpload"
                                    @library:selected="handleCollateralSelected"
                                >
                                    <template v-if="isNewFileUploaded" slot="actions">
                                        <app-icon-button
                                            class="delete-action"
                                            @click="handleDeleteNewFile"
                                        >
                                            <e25-deleted-items-svg color="#f30070" />
                                        </app-icon-button>
                                    </template>
                                </app-drag-and-drop-files>
                                <p v-if="!isNewFileUploaded" class="files-max-size">
                                    Maximum upload file size: <span class="amount">100 MB</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button :loading="isLoading" class="cancel-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="actions-container">
                    <app-button :loading="isLoading" class="save-btn" @click="handleSaveChanges">
                        Save
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import FileMixin, { FileByteSizeLimitError, FileTypeError } from '@/mixins/file-mixin';
import { FileService } from '@/scripts/services/file/file-service';
import { InvoiceProofOfPaymentServiceMixin } from '@/mixins/invoice-proof-of-payment-mixin';
import { LogLevel } from '@/scripts/logger';

const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppButton = () => import('@/components/common/app-button.vue');
const AppFilesPreview = () => import('@/components/common/app-files-preview');
const AppIconButton = () => import('@/components/common/app-icon-button');
const E25DeletedItemsSvg = () => import('@/components/svg/grid/e25-deleted-items-svg');
const AppDragAndDropFiles = () => import('@/components/common/app-drag-and-drop-files');

export default {
    name: 'edit-proof-of-payment-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-button': AppButton,
        'app-files-preview': AppFilesPreview,
        'app-icon-button': AppIconButton,
        'e25-deleted-items-svg': E25DeletedItemsSvg,
        'app-drag-and-drop-files': AppDragAndDropFiles,
    },

    mixins: [FileMixin, InvoiceProofOfPaymentServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,

            isLoadingFiles: false,

            isProofDeleted: false,

            fileConfig: {
                byteSizeMinimum: 0,
                byteSizeLimit: 100000000,
                fileSelectorAllowedTypes: ['pdf'],
                allowedImageTypes: ['application/pdf'],
            },

            initialProofOfPayment: {
                file: null,
                name: null,
                size: null,
                type: null,
            },

            proofOfPayment: {
                file: null,
                name: null,
                size: null,
                type: null,
            },

            isCollateral: false,
        };
    },

    computed: {
        title() {
            return this.widget.options.title;
        },

        accountId() {
            return this.widget.options.accountId;
        },

        invoiceId() {
            return this.widget.options.invoiceId;
        },

        isNewFileUploaded() {
            const initialFile = this.initialProofOfPayment.file;
            const currentFile = this.proofOfPayment.file;
            return initialFile && currentFile && initialFile !== currentFile;
        },
    },

    created() {
        this.getProofOfPaymentData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getProofOfPaymentData() {
            return this.dispatchProofOfPayment({
                invoiceId: this.invoiceId,
                accountId: this.accountId,
            })
                .then((data) => {
                    this.initialProofOfPayment = {
                        file: data.ProofUrl,
                        name: data.FileName,
                        size: data.FileSize,
                        type: data.FileMimeType,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'Error trying to get your proof of payment',
                        {
                            error,
                        },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handleUpload(e) {
            this.isLoadingFiles = true;
            this.getBase64Url(e, this.fileConfig)
                .then(({ encodingStr, name, size, type }) => {
                    this.proofOfPayment = {
                        file: encodingStr,
                        name,
                        size,
                        type,
                    };
                    this.isCollateral = false;
                })
                .catch((error) => {
                    if (error instanceof FileByteSizeLimitError) {
                        this.$helpers.showFailNotification(
                            'File size exceeds the maximum amount of 100Mbs',
                            { level: LogLevel.INFO },
                        );
                    } else if (error instanceof FileTypeError) {
                        const [head, ...tail] = this.fileConfig.fileSelectorAllowedTypes;
                        this.$helpers.showFailNotification(
                            `File needs to be of type ${tail.join(', ')} or ${head}`,
                            { level: LogLevel.INFO },
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your file. Please check your format and try again.',
                            { error },
                        );
                    }
                })
                .finally(() => {
                    this.isLoadingFiles = false;
                });
        },

        handleCollateralSelected(file) {
            const [selectedFile] = file;
            this.proofOfPayment = {
                file: selectedFile.url,
                name: selectedFile.previewName,
                size: selectedFile.size,
                type: selectedFile.type,
            };
            this.isCollateral = true;
        },

        handleDeleteNewFile() {
            this.proofOfPayment = {
                file: null,
                name: null,
                size: null,
                type: null,
            };
        },

        handleDeleteFile() {
            this.initialProofOfPayment = {
                file: null,
                name: null,
                size: null,
                type: null,
            };
            this.isProofDeleted = true;
        },

        handleSaveChanges() {
            this.isLoading = true;
            if (this.proofOfPayment.file) {
                return this.handleUploadProof();
            } else if (!this.initialProofOfPayment.file) {
                return this.handleDeleteProof();
            }
            this.closeWidget();
        },

        handleUploadProof() {
            if (this.isCollateral) {
                const proofPayload = {
                    accountId: this.accountId,
                    file: this.proofOfPayment,
                    invoiceId: this.invoiceId,
                };
                return this.handleUploadProofOfPayment(proofPayload)
                    .then(() => {
                        EventBus.$emit('proof-of-payment:update');
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'Error trying to save proof of payment',
                            {
                                error,
                            },
                        );
                    })
                    .finally(() => {
                        EventBus.$emit('invoice-details-update');
                        this.isLoading = false;
                    });
            }
            const filePayload = {
                name: this.proofOfPayment.name,
                url: this.proofOfPayment.file,
            };
            return FileService.post(filePayload)
                .then((response) => {
                    this.proofOfPayment.file = response.data;
                    const proofPayload = {
                        accountId: this.accountId,
                        file: this.proofOfPayment,
                        invoiceId: this.invoiceId,
                    };
                    return this.handleUploadProofOfPayment(proofPayload);
                })
                .then(() => {
                    EventBus.$emit('proof-of-payment:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Error trying to save proof of payment', {
                        error,
                    });
                })
                .finally(() => {
                    EventBus.$emit('invoice-details-update');
                    this.isLoading = false;
                });
        },

        handleDeleteProof() {
            this.deleteProofOfPayment({
                accountId: this.accountId,
                invoiceId: this.invoiceId,
            })
                .then(() => {
                    EventBus.$emit('proof-of-payment:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Error trying to delete proof of payment', {
                        error,
                    });
                })
                .finally(() => {
                    EventBus.$emit('invoice-details-update');
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.edit-proof-of-payment-widget .widget-container {
    width: 100%;
    height: 100%;
}

.edit-proof-of-payment-widget .widget-container .content-wrapper {
    padding: 23px 33px;
}

.edit-proof-of-payment-widget .content-wrapper .loading-screen {
    text-align: center;
}

.edit-proof-of-payment-widget .proof-file-container,
.edit-proof-of-payment-widget .proof-deleted-container {
    box-shadow: 0px 3px 6px #8274e833;
    border: 1px solid #f4f5f9;
    border-radius: 12px;
    padding: 25px;
    margin-top: 20px;
}

.edit-proof-of-payment-widget .proof-file-container .app-files-preview .file-preview {
    min-width: 262px;
    width: fit-content;
}

.edit-proof-of-payment-widget .proof-deleted-container {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 20px;
}

.edit-proof-of-payment-widget .proof-deleted-container p {
    font-weight: 600;
    margin: 0;
}

.edit-proof-of-payment-widget .app-icon-button,
.edit-proof-of-payment-widget .delete-icon {
    padding: 4px 8px;
}

.edit-proof-of-payment-widget .app-icon-button:hover {
    background-color: #f5d5e7;
}

.edit-proof-of-payment-widget .delete-icon {
    background-color: #f5d5e7;
    border-radius: 50%;
}

.edit-proof-of-payment-widget .app-icon-button svg,
.edit-proof-of-payment-widget .delete-icon svg {
    position: relative;
    top: 3px;
    width: 24px;
    height: 24px;
}

.edit-proof-of-payment-widget .app-files-preview .file-preview {
    padding: 9px 12px;
}

.edit-proof-of-payment-widget .files-max-size {
    color: #877d88;
    padding-top: 16px;
}

.edit-proof-of-payment-widget .files-max-size .amount {
    font: normal normal 500 16px Roboto;
}

.edit-proof-of-payment-widget .cancel-btn {
    background: transparent;
    color: #382153;
    border: 1px solid #cac6cb;
    width: 127px;
}

.edit-proof-of-payment-widget .cancel-btn:hover {
    background: #e1e0e5;
}

.edit-proof-of-payment-widget .save-btn {
    width: 170px;
}
</style>
